import { useState } from 'react';
import { toValidationReportBlob, toValidationReportJson } from '../../core/export/validationReport.ts';
import type { Severity, ValidationIssue } from '../../core/model/issues.ts';
import type { ValidationReport } from '../../core/validator/report.ts';
import { useI18n } from '../../i18n/index.tsx';
import { Button } from '../../ui/primitives.tsx';
import { downloadBlob } from '../../utils/download.ts';

const SEVERITY_ORDER: readonly Severity[] = ['error', 'warning', 'info'];

/**
 * What was wrong with the file, in a form someone can act on.
 *
 * The promise this screen exists to keep is that a rejected file never produces the words "invalid
 * JSON" and nothing else. Every entry says which question, what was expected, what was found, and —
 * where the validator knows one — what to do about it.
 */
export function ValidationReportView({
  report,
  onRetry,
}: {
  readonly report: ValidationReport;
  readonly onRetry: () => void;
}) {
  const { t, issue: describe, hint } = useI18n();
  const [copied, setCopied] = useState(false);

  const grouped = SEVERITY_ORDER.map((severity) => ({
    severity,
    issues: report.issues.filter((entry) => entry.severity === severity),
  })).filter((group) => group.issues.length > 0);

  const copy = async (): Promise<void> => {
    await navigator.clipboard.writeText(JSON.stringify(toValidationReportJson(report), null, 2));
    setCopied(true);
    window.setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  return (
    <section className="screen screen--report" aria-labelledby="report-title">
      <h1 id="report-title">{report.ok ? t('report.titleWarnings') : t('report.title')}</h1>

      <p className="lede">
        {t('report.summary', { errors: report.counts.error, warnings: report.counts.warning })}
      </p>

      {report.issues.length === 0 && <p>{t('report.noIssues')}</p>}

      {grouped.map((group) => (
        <div key={group.severity} className={`issues issues--${group.severity}`}>
          <h2>{t(`report.severity.${group.severity}` as const)}</h2>
          <ul className="issues__list">
            {group.issues.map((entry, index) => (
              <IssueRow
                key={`${entry.code}-${entry.path}-${String(index)}`}
                issue={entry}
                message={describe(entry)}
                hint={hint(entry)}
              />
            ))}
          </ul>
        </div>
      ))}

      {report.suppressedCount > 0 && (
        <p className="issues__suppressed">
          {t('report.suppressed', { count: report.suppressedCount })}
        </p>
      )}

      <div className="actions">
        <Button variant="primary" onClick={onRetry}>
          {t('report.tryAnother')}
        </Button>
        <Button
          onClick={() => {
            downloadBlob(toValidationReportBlob(report), 'validation-report.json');
          }}
        >
          {t('report.download')}
        </Button>
        <Button
          onClick={() => {
            void copy();
          }}
        >
          {copied ? t('report.copied') : t('report.copy')}
        </Button>
      </div>
    </section>
  );
}

function IssueRow({
  issue,
  message,
  hint,
}: {
  readonly issue: ValidationIssue;
  readonly message: string;
  readonly hint: string | undefined;
}) {
  const { t } = useI18n();

  return (
    <li className="issue">
      <p className="issue__message">{message}</p>

      <dl className="issue__details">
        {issue.questionId !== undefined && (
          <>
            <dt>{t('report.question', { id: '' }).trim()}</dt>
            <dd>
              <code>{issue.questionId}</code>
            </dd>
          </>
        )}
        {issue.expected !== undefined && (
          <>
            <dt>{t('report.expected')}</dt>
            <dd>{issue.expected}</dd>
          </>
        )}
        {issue.received !== undefined && (
          <>
            <dt>{t('report.received')}</dt>
            <dd>{issue.received}</dd>
          </>
        )}
        {issue.path !== '' && (
          <>
            <dt>{t('report.location')}</dt>
            <dd>
              <code>{issue.path}</code>
            </dd>
          </>
        )}
      </dl>

      {hint !== undefined && <p className="issue__hint">{hint}</p>}
      <p className="issue__code">
        <code>{issue.code}</code>
      </p>
    </li>
  );
}
